import 'Stores/dev-elite-performance/search.css';

import _forIn from 'lodash-es/forIn.js';
import { initializeAddons } from 'Addons';
import { fitmentSearchEnabled } from '!!extract-features!Stores/dev-elite-performance/config.js';

import getFitmentSearchAPI from 'Addons/fitmentSearch/fitmentSearchAPI.ts';
import history, { uriChangeHandled } from 'Core/history.js';
import store from 'Core/store.ts';
import { clearAutocompleteQuery } from 'Core/actions/autocomplete.ts';
import { setFitmentTableResponse } from 'Core/actions/fitmentSearch/index.js';
import { setExtractedColors, setProductData } from 'Core/actions/product.ts';
import { replaceInHistory } from 'Core/actions/redirect.ts';
import {
  discardQuery,
  filterQueryResetRequested,
  replaceRequest,
  resetRequest,
} from 'Core/actions/request.js';
import { autocompleteRequestQuerySelector } from 'Core/selectors/autocomplete.ts';
import { searchRequestQuerySelector, searchRequestFilterQuerySelector } from 'Core/selectors/search.js';
import productConfig from 'Models/uiConfig/productConfig.js';
import requestConfig from 'Models/uiConfig/requestConfig.js';
import uiConfig from 'Models/uiConfig/uiConfig.js';
import { getRequestFromUri, getUriFromRequest } from 'Modules/converter/index.js';
import {
  extractColorsByUrl,
  item as getItemData,
  fitments as getFitments,
  track,
  loadGarageFromAccount,
} from 'Modules/serverApi/index.js';
import { addProductsHistory, isLoggedInStateChanged } from 'Utils/userStorage.js';

export function setupPage() {
  setBrowserGlobals();
  checkDev();
}

function setBrowserGlobals() {
  const cmGlobal = window.Convermax || (window.Convermax = {});
  const fitmentSearchAPI = fitmentSearchEnabled ? getFitmentSearchAPI(store) : {};

  _forIn(
    {
      version: CONVERMAX_VERSION,
      store: cmGlobal.store || {},
      trackProductView: () => {
        /* back compatibility */
      },
      trackAddToCart: (productId) => {
        track('AddToCart', { ProductId: productId });
      },
      getSearchRequest: () => store.getState().search.request,
      getSearchResponse: () => store.getState().search.response,
      addProductsHistory,
      createSearch: (request) => getUriFromRequest(request).search,
      createSearchUrl: (request) => getUriFromRequest(request).href,
      clearCurrentQuery: () => {
        const state = store.getState();

        if (autocompleteRequestQuerySelector(state)) {
          store.dispatch(clearAutocompleteQuery());
        }

        if (searchRequestQuerySelector(state)) {
          store.dispatch(discardQuery());
        }

        if (searchRequestFilterQuerySelector(state)) {
          store.dispatch(filterQueryResetRequested());
        }
      },
      clearCurrentRequest: (doNotDiscardVehicle) => {
        store.dispatch(
          resetRequest({ discardUserPreselection: !doNotDiscardVehicle, allowSearchSameAsUri: true }),
        );
      },
      getProductAsync: (id) => getItemData(id),
      ...fitmentSearchAPI,
    },
    (value, key) => (cmGlobal[key] = value),
  );
}

function checkDev() {
  if (location.hostname !== 'localhost') {
    // eslint-disable-next-line no-console
    console.log(__('devVersionConsoleWarn'));
  }
}

export function runWhenReady(renderPage) {
  if (uiConfig.deferInit) {
    document.addEventListener('DOMContentLoaded', () => CONVERMAX_INITIALIZE(renderPage));
  } else {
    CONVERMAX_INITIALIZE(renderPage);
  }
}

// initialization

function CONVERMAX_INITIALIZE(renderPage) {
  initializeAddons(store);

  renderPage();

  if (requestConfig.needInitRequest) {
    setUriRequest(window.location, true);
  }
  subscribeToHistory();

  makeInitRequests();

  uiConfig.afterInit();

  window.Convermax.initialized = true;
}

// base init

function subscribeToHistory() {
  history.listen(({ location, action }) => {
    if (action === 'REPLACE') {
      return;
    }

    const requestNeeded = location.state !== uriChangeHandled && requestConfig.hasSearchResults;
    if (requestNeeded) {
      setUriRequest(location);
    } else {
      store.dispatch(replaceInHistory(location, null));
    }
  });
}

function setUriRequest(location, onInit = false) {
  store.dispatch(replaceRequest({ ...getRequestFromUri(location), fromUri: true, onInit }));
}

function makeInitRequests() {
  const { getImageToExtractColors, localItemId } = productConfig;

  if (localItemId) {
    track('ProductView', { ProductId: localItemId });

    if (requestConfig.needInitItemRequest) {
      getItemData(localItemId).then((item) => store.dispatch(setProductData(item)));
    }

    if (getImageToExtractColors) {
      const imageToExtract = productConfig.getImageToExtractColors();

      if (imageToExtract) {
        extractColorsByUrl(imageToExtract).then((colors) => store.dispatch(setExtractedColors(colors)));
      }
    }

    if (requestConfig.needInitFitmentTableRequest) {
      getFitments(localItemId).then((response) => store.dispatch(setFitmentTableResponse(response)));
    }
  }

  if (requestConfig.getCustomerAsync) {
    syncCustomerGarage();
  }
}

async function syncCustomerGarage() {
  try {
    const customer = await requestConfig.getCustomerAsync();
    const isLoggedIn = !!customer;

    if (isLoggedInStateChanged(isLoggedIn) && customer?.id) {
      const vehicleData = await getCustomerGarage(customer.id);

      if (vehicleData?.Garage) {
        getFitmentSearchAPI(store).addVehicleList(vehicleData.Garage);
      }
    }
  } catch (e) {
    console.error("Failed to sync customer's garage", e);
  }
}

function getCustomerGarage(customerId) {
  if (customerId) {
    return loadGarageFromAccount(customerId);
  }
}
